import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone, Calendar, Check, X, Star, Award, Heart, ShieldCheck,
  Users, Stethoscope, MapPin, Clock,
} from 'lucide-react';

const PHONE_DISPLAY = '(817) 985-3337';
const PHONE_TEL = '817-985-3337';

const comparison = [
  { feature: 'Locally owned & operated since 1990', fwd: true, chain: false },
  { feature: 'See the same trusted dentist every visit', fwd: true, chain: false },
  { feature: 'Treatment plans based on your needs — not sales quotas', fwd: true, chain: false },
  { feature: 'Board-certified implant specialist on staff (Diplomate, ABOI)', fwd: true, chain: false },
  { feature: 'Decisions made in-office, not by a corporate HQ', fwd: true, chain: false },
  { feature: '1,100+ verified 5-star patient reviews', fwd: true, chain: false },
  { feature: 'Doctors who live and serve in the Fort Worth community', fwd: true, chain: false },
  { feature: 'Big advertising budgets bidding on competitor names', fwd: false, chain: true },
];

const reasons = [
  { icon: Heart, title: 'Independently Owned', desc: 'We answer to our patients — not shareholders or a national chain. Your care always comes first.' },
  { icon: Stethoscope, title: 'The Same Doctors, Every Time', desc: 'Dr. Mund and Dr. Razo know you by name. No rotating roster of unfamiliar providers.' },
  { icon: Award, title: '30+ Years in Fort Worth', desc: 'A trusted neighborhood practice since 1990, with thousands of families served across DFW.' },
  { icon: ShieldCheck, title: 'Honest, No-Pressure Care', desc: 'No sales scripts, no upselling quotas. Just clear options and a personalized plan you can trust.' },
];

const WhyChooseFortWorthDental = () => {
  return (
    <div data-testid="why-choose-page">
      <Helmet>
        <title>Why Choose Fort Worth Dental | Locally Owned, Not a Corporate Chain</title>
        <meta name="description" content="Fort Worth Dental is a locally owned practice serving Fort Worth since 1990. See the same trusted dentists every visit, with 1,100+ 5-star reviews and honest, no-pressure care. Call (817) 985-3337." />
        <meta name="keywords" content="Fort Worth Dental, locally owned dentist fort worth, independent dentist fort worth, family dentist fort worth, fort worth dental reviews" />
        <link rel="canonical" href="https://fortworthdental.com/why-choose-fort-worth-dental" />
        <meta property="og:title" content="Why Choose Fort Worth Dental | Locally Owned Since 1990" />
        <meta property="og:description" content="A trusted, independently owned Fort Worth dental practice — not a corporate chain. 1,100+ 5-star reviews." />
        <meta property="og:url" content="https://fortworthdental.com/why-choose-fort-worth-dental" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Dentist',
            name: 'Fort Worth Dental',
            description: 'Locally owned Fort Worth dental practice serving the community since 1990 with 1,100+ 5-star reviews.',
            url: 'https://fortworthdental.com/why-choose-fort-worth-dental',
            telephone: '+1-817-985-3337',
            foundingDate: '1990',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '4620 Citylake Blvd W',
              addressLocality: 'Fort Worth',
              addressRegion: 'TX',
              postalCode: '76132',
              addressCountry: 'US',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.9',
              bestRating: '5',
              reviewCount: '1100',
            },
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-800 to-slate-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20">
              <Heart className="w-5 h-5 text-teal-200" />
              <span className="text-sm font-medium tracking-wide">Locally owned & trusted since 1990</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              The Real <span className="text-teal-300">Fort Worth Dental</span>
            </h1>
            <p className="text-lg md:text-xl text-teal-50/90 mb-8 max-w-2xl mx-auto">
              You searched for us by name for a reason. We're the independently owned
              Fort Worth practice with the same trusted doctors, 1,100+ 5-star reviews,
              and care that puts you first — never a corporate chain.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="why-hero-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-teal-800 font-bold py-4 px-8 rounded-full text-lg hover:bg-teal-50 transition-colors shadow-lg"
              >
                <Phone className="w-5 h-5" />
                {PHONE_DISPLAY}
              </a>
              <Link
                to="/contact-us"
                data-testid="why-hero-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                <Calendar className="w-5 h-5" />
                Book Your Visit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-slate-900 text-white py-6 border-t border-white/10">
        <div className="container mx-auto px-4 flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 fill-teal-300 text-teal-300" />
            <span className="font-semibold">1,100+ 5-Star Reviews</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-300" />
            <span className="font-semibold">30+ Years in Fort Worth</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-teal-300" />
            <span className="font-semibold">Board-Certified Implant Specialist</span>
          </div>
        </div>
      </section>

      {/* Reasons grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-slate-800">
            Why Patients <span className="text-teal-700">Choose Us</span>
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Other practices may advertise on our name — but here's what they can't offer.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {reasons.map((r, idx) => {
              const Icon = r.icon;
              return (
                <div key={idx} data-testid={`why-reason-${idx}`} className="bg-slate-50 rounded-2xl p-6 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-teal-700" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2">{r.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{r.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-slate-800">
            Fort Worth Dental vs. Corporate Chains
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            A side-by-side look at what makes an independently owned practice different.
          </p>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="why-comparison-table">
            <div className="grid grid-cols-12 bg-teal-700 text-white font-semibold text-sm md:text-base">
              <div className="col-span-8 p-4">What matters to you</div>
              <div className="col-span-2 p-4 text-center">Fort Worth Dental</div>
              <div className="col-span-2 p-4 text-center">Corporate Chain</div>
            </div>
            {comparison.map((row, idx) => (
              <div key={idx} className={`grid grid-cols-12 items-center text-sm md:text-base ${idx % 2 ? 'bg-slate-50' : 'bg-white'}`}>
                <div className="col-span-8 p-4 text-slate-700">{row.feature}</div>
                <div className="col-span-2 p-4 flex justify-center">
                  {row.fwd
                    ? <Check className="w-5 h-5 text-teal-600" />
                    : <X className="w-5 h-5 text-slate-300" />}
                </div>
                <div className="col-span-2 p-4 flex justify-center">
                  {row.chain
                    ? <Check className="w-5 h-5 text-slate-400" />
                    : <X className="w-5 h-5 text-slate-300" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-800">
            Meet Your Fort Worth Dentists
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-slate-50 p-8 rounded-2xl text-center">
              <div className="w-28 h-28 bg-teal-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Stethoscope className="w-14 h-14 text-teal-700" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Dr. Arthur Mund, DDS</h3>
              <p className="text-teal-700 font-semibold mb-2">Diplomate, ABOI</p>
              <p className="text-slate-600 text-sm">
                Board-certified implant specialist with 25+ years of experience, known
                for a gentle approach and life-changing results.
              </p>
            </div>
            <div className="bg-slate-50 p-8 rounded-2xl text-center">
              <div className="w-28 h-28 bg-teal-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Stethoscope className="w-14 h-14 text-teal-700" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Dr. David Razo, DDS</h3>
              <p className="text-teal-700 font-semibold mb-2">General & Cosmetic Dentistry</p>
              <p className="text-slate-600 text-sm">
                Expert in cosmetic dentistry, Invisalign, and comprehensive family care
                for patients of every age.
              </p>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/our-team" data-testid="why-meet-team-link" className="text-teal-700 hover:text-teal-800 font-semibold">
              Meet Our Full Team →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-700 to-teal-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Come See the Difference for Yourself
          </h2>
          <p className="text-lg text-teal-50/90 mb-8 max-w-2xl mx-auto">
            Schedule your visit with the original, locally owned Fort Worth Dental.
            We'd be honored to care for your smile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="why-cta-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-800 font-bold py-4 px-8 rounded-full text-lg hover:bg-teal-50 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call {PHONE_DISPLAY}
            </a>
            <Link
              to="/contact-us"
              data-testid="why-cta-book-btn"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Schedule Online
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-teal-50/80 text-sm">
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> 4620 Citylake Blvd W, Fort Worth, TX 76132</span>
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> Mon–Fri, 8:00 AM – 5:00 PM</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseFortWorthDental;
